import { v4 as uuidv4 } from 'uuid'
import { datasetSeriesPreviewValue } from '../utils/datasetPreview.js'

// Designer-only identity. It is deliberately a Symbol so it can never leak
// into JSONL, while Vue/Konva can still use it as a stable list key during the
// lifetime of a loaded Designer object.
const DESIGNER_ID = Symbol('gaugeNeedleDesignerId')
const PREVIEW_NEEDLES = new WeakMap()
const NEEDLE_PROPERTY = /^needles\[(\d+)\]\.(val|color)$/

export function ensureGaugeNeedleId(needle) {
    if (!needle || typeof needle !== 'object') return null
    if (!needle[DESIGNER_ID]) {
        Object.defineProperty(needle, DESIGNER_ID, {
            value: uuidv4(),
            enumerable: false,
            configurable: false,
            writable: false,
        })
    }
    return needle[DESIGNER_ID]
}

export function getGaugeNeedleId(needle) {
    return ensureGaugeNeedleId(needle)
}

export function ensureGaugeNeedleIds(needles) {
    if (!Array.isArray(needles)) return []
    needles.forEach(ensureGaugeNeedleId)
    return needles
}

export function isGaugeMultipleMode(node) {
    return Array.isArray(node?.needles)
}

/**
 * Parse the Designer/runtime spelling of one indexed needle property.
 * The returned index is the current LVGL needle index, i.e. array order.
 */
export function parseGaugeNeedleProperty(name) {
    const match = NEEDLE_PROPERTY.exec(String(name ?? ''))
    return match ? { index: Number(match[1]), property: match[2] } : null
}

/**
 * Dynamic scalar descriptors used by the action and binding editors. They
 * deliberately carry the wire spelling as both `name` and `wireKey`; no
 * Designer-only identity is ever part of the wire key.
 */
export function getGaugeNeedleActionProperties(node) {
    if (!isGaugeMultipleMode(node)) return []
    ensureGaugeNeedleIds(node.needles)
    return node.needles.flatMap((needle, index) => [
        {
            key: `object:needles[${index}].val`,
            property: { part: null, name: `needles[${index}].val`, needleId: getGaugeNeedleId(needle) },
            label: `Needle ${index} value`,
            type: 'int',
            metadata: { type: 'int', min: Number(node.min), max: Number(node.max) },
            wireKey: `needles[${index}].val`,
        },
        {
            key: `object:needles[${index}].color`,
            property: { part: null, name: `needles[${index}].color`, needleId: getGaugeNeedleId(needle) },
            label: `Needle ${index} color`,
            type: 'color',
            metadata: { type: 'color' },
            wireKey: `needles[${index}].color`,
        },
    ])
}

/** Binding names for the current collection. Color is included because the
 * existing binding editor already has a first-class color type/formatter. */
export function getGaugeNeedleBindableProperties(node) {
    return getGaugeNeedleActionProperties(node).map(item => item.property.name)
}

/** Resolve a dynamic reference against the current collection. A stored
 * needleId keeps a Designer action attached to the same item across reorder;
 * an index-only reference remains compatible with parsed wire commands. */
export function resolveGaugeNeedleProperty(node, reference) {
    if (!isGaugeMultipleMode(node) ||
        (reference?.part !== null && reference?.part !== undefined)) return null
    const parsed = parseGaugeNeedleProperty(reference?.name)
    if (!parsed) return null
    ensureGaugeNeedleIds(node.needles)
    let index = parsed.index
    if (reference?.needleId) {
        index = node.needles.findIndex(needle => getGaugeNeedleId(needle) === reference.needleId)
        if (index < 0) return null
    }
    const needle = node.needles[index]
    if (!needle) return null
    return { index, property: parsed.property, needle }
}

/** The legacy `.val` action remains the public alias for needle zero once a
 * Gauge is in multiple mode. It is only used by preview mutation; its wire
 * spelling remains the existing `.val` command. */
export function resolveGaugeNeedlePreviewProperty(node, reference) {
    if (!isGaugeMultipleMode(node) ||
        (reference?.part !== null && reference?.part !== undefined)) return null
    if (reference?.name === 'val') {
        const needle = node.needles[0]
        return needle ? { index: 0, property: 'val', needle, alias: true } : null
    }
    return resolveGaugeNeedleProperty(node, reference)
}

function cloneNeedles(needles) {
    return needles.map(needle => ({ ...needle }))
}

/** Runtime-only preview storage. This prevents action simulation from
 * changing the configured JSONL export, just like HaspObject's flat-property
 * preview storage does for ordinary properties. */
export function setGaugeNeedlePreviewProperty(node, reference, value) {
    const target = resolveGaugeNeedlePreviewProperty(node, reference)
    if (!target) return false
    if (!PREVIEW_NEEDLES.has(node)) PREVIEW_NEEDLES.set(node, cloneNeedles(node.needles))
    target.needle[target.property] = value
    return true
}

export function getConfiguredGaugeNeedles(node) {
    return PREVIEW_NEEDLES.get(node) ?? node?.needles
}

/** Discard a runtime action preview and restore the configured needle leaves. */
export function discardGaugeNeedlePreview(node) {
    const base = PREVIEW_NEEDLES.get(node)
    if (!base || !Array.isArray(node?.needles)) return
    node.needles.forEach((needle, index) => {
        if (!base[index]) return
        needle.val = base[index].val
        needle.color = base[index].color
    })
    PREVIEW_NEEDLES.delete(node)
}

export function commitGaugeNeedlePreview(node) {
    PREVIEW_NEEDLES.delete(node)
}

export function clampGaugeValue(value, min, max) {
    const lower = Number(min)
    const upper = Number(max)
    let result = Number(value)
    if (!Number.isFinite(result)) result = Number.isFinite(lower) ? lower : 0
    if (Number.isFinite(lower) && result < lower) result = lower
    if (Number.isFinite(upper) && result > upper) result = upper
    return result
}

/** Build the effective LVGL needle list for an object-level dataset. */
export function getGaugeDatasetNeedles(dataset, min, max, fallbackColor) {
    return (dataset?.series ?? []).map((series, index) => ({
        index,
        source: series,
        label: series.label || `Series ${index + 1}`,
        val: clampGaugeValue(
            datasetSeriesPreviewValue(dataset, series, index),
            min,
            max,
        ),
        color: series.color || fallbackColor,
    }))
}

/**
 * Return the effective ordered needles without changing the legacy storage.
 * In legacy mode this creates a virtual needle 0 from val and indicator color.
 */
export function getEffectiveGaugeNeedles(node, fallbackColor, resolveDataset) {
    if (node?.dataset && resolveDataset) {
        const dataset = resolveDataset(node.dataset)
        if (dataset) return getGaugeDatasetNeedles(dataset, node.min, node.max, fallbackColor)
        // A configured but missing dataset remains authoritative: do not
        // silently render the preserved manual fallback as if it were live.
        return []
    }

    if (!isGaugeMultipleMode(node)) {
        return [{
            index: 0,
            source: null,
            val: clampGaugeValue(node?.val, node?.min, node?.max),
            color: fallbackColor,
        }]
    }

    ensureGaugeNeedleIds(node.needles)
    const previewBase = PREVIEW_NEEDLES.get(node)
    return node.needles.map((needle, index) => {
        const valuePreviewOverride = previewBase?.[index] && needle.val !== previewBase[index].val
            ? needle.val
            : undefined
        const colorPreviewOverride = previewBase?.[index] && needle.color !== previewBase[index].color
            ? needle.color
            : undefined
        return {
            index,
            source: needle,
            val: clampGaugeValue(valuePreviewOverride ?? needle?.val, node?.min, node?.max),
            color: colorPreviewOverride ?? needle?.color ?? fallbackColor,
        }
    })
}

/** Apply the same persistent clamping behavior the legacy Gauge uses. */
export function normalizeGaugeNeedleValues(node) {
    if (isGaugeMultipleMode(node)) {
        ensureGaugeNeedleIds(node.needles)
        node.needles.forEach(needle => {
            needle.val = clampGaugeValue(needle?.val, node.min, node.max)
        })
    } else {
        node.val = clampGaugeValue(node.val, node.min, node.max)
    }
    return node
}

export function materializeLegacyGaugeNeedle(node, fallbackColor) {
    if (!isGaugeMultipleMode(node)) {
        node.needles = [{
            val: clampGaugeValue(node.val, node.min, node.max),
            color: fallbackColor,
        }]
    }
    ensureGaugeNeedleIds(node.needles)
    return node.needles
}

export function captureGaugeManualState(node, indicatorColor) {
    return {
        hasNeedles: Array.isArray(node?.needles),
        val: node?.val,
        needles: Array.isArray(node?.needles)
            ? node.needles.map(needle => ({ val: needle.val, color: needle.color }))
            : null,
        indicatorColor,
    }
}

export function restoreGaugeManualState(node, state, setIndicatorColor) {
    if (!state) return node
    node.val = state.val
    if (state.hasNeedles) {
        node.needles = state.needles.map(needle => ({ ...needle }))
        ensureGaugeNeedleIds(node.needles)
    } else {
        delete node.needles
    }
    if (state.indicatorColor !== undefined) setIndicatorColor?.(state.indicatorColor)
    return node
}

export function gaugeNeedleAngle({ value, min, max, angle, rotation = 0, rotOffset = -90 }) {
    const range = Number(max) - Number(min)
    if (!Number.isFinite(range) || range === 0) return rotOffset + Number(rotation) - (Number(angle) / 2)
    return (((Number(value) - Number(min)) / range) * Number(angle))
        + rotOffset + Number(rotation) - (Number(angle) / 2)
}

export function getGaugeNeedleRenderPlan(node, indicatorColor, resolveDataset) {
    const effective = getEffectiveGaugeNeedles(node, indicatorColor, resolveDataset)
    return {
        needles: effective.map(needle => ({
            ...needle,
            angle: gaugeNeedleAngle({
                value: needle.val,
                min: node.min,
                max: node.max,
                angle: node.hasp_angle,
                rotation: node.hasp_rotation,
            }),
        })),
        pivotCount: 1,
    }
}

export function serializeGaugeNeedles(needles) {
    return (Array.isArray(needles) ? needles : []).map(needle => ({
        val: needle.val,
        color: needle.color,
    }))
}

export function applyGaugeNeedlesExport(needles, objectData) {
    if (Array.isArray(needles) && needles.length > 0) {
        // Once the collection exists, val is no longer an independent source
        // of truth. Keep legacy val on legacy objects only.
        delete objectData.val
        objectData.needles = serializeGaugeNeedles(needles)
    }
    return objectData
}

export function removeGaugeNeedle(needles, index) {
    if (!Array.isArray(needles) || needles.length <= 1) return needles ?? []
    return needles.filter((_needle, needleIndex) => needleIndex !== index)
}

export function moveGaugeNeedle(needles, index, delta) {
    if (!Array.isArray(needles)) return []
    const target = index + delta
    if (index < 0 || index >= needles.length || target < 0 || target >= needles.length) return needles
    const next = [...needles]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved)
    return next
}
